<template>
  <div id="file-storage" class="flex-shrink flex-grow overflow-auto p-4">
    <div class="flex flex-col">
      <div class="flex flex-col h-full">
        <div class="flex items-center justify-center py-2 px-4 bg-woot-50 border border-woot-100 text-sm mb-4">
          <span>
            {{ $t('FILE_STORAGE.SELECT_ALL.STORAGE_USAGE', storageUsage) }}
          </span>
        </div>
        <div class="bg-white flex-1 h-full">
          <ve-table
            :fixed-header="true"
            :columns="columns"
            :table-data="tableData"
            :border-around="false"
            :sort-option="sortOption"
            :checkbox-option="checkboxOption"
            row-key-field-name="id"
          />
          <div
            v-if="showSelectAllStorageBanner"
            class="flex items-center justify-center py-2 px-4 bg-woot-50 border-b border-woot-100 text-sm"
          >
            <span v-if="isSelectAllStorage">
              {{
                $t('FILE_STORAGE.SELECT_ALL.ALL_STORAGE_SELECTED', {
                  total: meta.all_count,
                })
              }}
              <button
                class="ml-2 text-woot-600 underline font-medium hover:text-woot-700"
                @click="onClearSelection"
              >
                {{ $t('FILE_STORAGE.SELECT_ALL.CLEAR_SELECTION') }}
              </button>
            </span>
            <span v-else>
              {{
                $t('FILE_STORAGE.SELECT_ALL.PAGE_SELECTED', {
                  pageCount: tableData.length,
                })
              }}
              <button
                class="ml-2 text-woot-600 underline font-medium hover:text-woot-700 cursor-pointer"
                @click="onSelectAllStorage"
              >
                {{
                  $t('FILE_STORAGE.SELECT_ALL.SELECT_ALL_IN_STORAGE', {
                    total: meta.all_count,
                  })
                }}
              </button>
            </span>
          </div>
          <empty-state
            v-if="showEmptyState"
            :title="$t('FILE_STORAGE.LIST.NO_RECORDS')"
          />
          <div
            v-if="loading"
            class="items-center flex text-base justify-center"
          >
            <spinner />
            <span>{{ $t('FILE_STORAGE.LIST.LOADING_MESSAGE') }}</span>
          </div>
        </div>
        <table-footer
          :current-page="Number(meta.current_page)"
          :total-count="meta.all_count"
          :page-size="25"
          @page-change="onPageChange"
        />
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { VeTable } from 'vue-easytable';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import Spinner from 'shared/components/Spinner.vue';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import rtlMixin from 'shared/mixins/rtlMixin';
import timeMixin from 'dashboard/mixins/time';

export default {
  name: 'FileStorageIndex',
  components: {
    Spinner,
    EmptyState,
    VeTable,
    TableFooter,
  },
  mixins: [rtlMixin, timeMixin],
  data() {
    return {
      sortOption: {
        sortAlways: true,
        // eslint-disable-next-line no-return-assign
        sortChange: params => {
          this.sortConfig = params;
          this.$store.dispatch('fileStorage/fetchRecords');
        },
      },
      checkboxOption: {
        selectedRowKeys: [],
        selectedRowChange: ({ selectedRowKeys }) => {
          this.checkboxOption.selectedRowKeys = selectedRowKeys;

          if (
            this.isSelectAllStorage &&
            selectedRowKeys.length < this.tableData.length
          ) {
            this.$store.commit('fileStorage/SET_SELECT_ALL_STORAGE', false);
          }

          this.$store.commit('fileStorage/SET_SELECTED_ROWS', {
            selectedRowKeys: selectedRowKeys,
            page: this.meta.current_page,
          });
        },
        selectedAllChange: ({ selectedRowKeys }) => {
          this.checkboxOption.selectedRowKeys = selectedRowKeys;

          if (selectedRowKeys.length === 0 && this.isSelectAllStorage) {
            this.$store.commit('fileStorage/SET_SELECT_ALL_STORAGE', false);
          }

          this.$store.commit('fileStorage/SET_SELECTED_ROWS', {
            selectedRowKeys: selectedRowKeys,
            page: this.meta.current_page,
          });
        },
      },
      columns: [],
    };
  },
  computed: {
    ...mapGetters({
      attachments: 'fileStorage/getAttachments',
      meta: 'fileStorage/getMeta',
      loading: 'fileStorage/getLoading',
      getSortConfig: 'fileStorage/getSortConfig',
      currentAccountId: 'getCurrentAccountId',
      getAccount: 'accounts/getAccount',
      selectedRowsInPage: 'fileStorage/getSelectedRowsInPage',
      isSelectAllStorage: 'fileStorage/isSelectAllStorage',
    }),
    sortConfig: {
      get() {
        return this.getSortConfig;
      },
      set(value) {
        this.$store.commit('fileStorage/SET_SORT_CONFIG', value);
      },
    },
    showEmptyState() {
      return !this.loading && this.attachments?.length === 0;
    },
    tableData() {
      if (this.loading) {
        return [];
      }
      return (this.attachments || []).map(
        ({
          location,
          location_metadata,
          created_at,
          filename,
          byte_size,
          url,
          id,
        }) => {
          return {
            location,
            id,
            url,
            created_at: this.messageStamp(created_at, 'dd.MM.yyyy h:mm:ss'),
            filename,
            conversationUrl: this.makeConversationUrl(location_metadata || {}),
            byte_size: `${(byte_size / 1024 / 1024).toFixed(2)}`,
          };
        }
      );
    },
    isAllCurrentPageSelected() {
      return (
        this.tableData.length > 0 &&
        this.checkboxOption.selectedRowKeys.length === this.tableData.length
      );
    },
    showSelectAllStorageBanner() {
      return (
        !this.loading &&
        this.tableData.length > 0 &&
        (this.isSelectAllStorage || this.isAllCurrentPageSelected) &&
        this.meta.all_count > this.tableData.length
      );
    },
    storageUsage() {
      const bytes = Math.max(0, this.meta?.storage_usage ?? 0);
      const units = [
        { unit: 'TB', threshold: 1099511627776 },
        { unit: 'GB', threshold: 1073741824 },
        { unit: 'MB', threshold: 1048576 },
      ];
      const { unit, threshold } =
        units.find(({ threshold: t }) => bytes >= t) ?? units[2];
      return { amount: parseFloat((bytes / threshold).toFixed(2)), unit };
    },
  },
  watch: {
    $route() {
      this.$store.dispatch('fileStorage/fetchRecords');
    },
    loading(value) {
      if (!value) {
        this.$nextTick(() => {
          if (this.isSelectAllStorage) {
            this.checkboxOption.selectedRowKeys = this.tableData.map(r => r.id);
          } else {
            const saved = this.selectedRowsInPage[this.meta.current_page] || [];
            this.checkboxOption.selectedRowKeys = [...saved];
          }
        });
      }
    },
    '$i18n.locale'() {
      this.updateColumnTitles();
    },
  },
  created() {
    this.columns = this.buildColumns();
  },
  async mounted() {
    this.$store.dispatch('fileStorage/fetchRecords');
  },
  methods: {
    buildColumns() {
      return [
        {
          field: '',
          key: 'a',
          type: 'checkbox',
          title: '',
          width: 50,
          align: 'center',
        },
        {
          field: 'filename',
          key: 'filename',
          sortBy: this.sortConfig.filename || '',
          title: this.$t('FILE_STORAGE.LIST.TABLE_HEADER.FILENAME'),
          fixed: 'left',
          align: this.isRTLView ? 'right' : 'left',
          renderBodyCell: ({ row }) => (
            <a href={row.url} target="_blank">
              {row.filename}
            </a>
          ),
        },
        {
          field: 'byte_size',
          key: 'byte_size',
          sortBy: this.sortConfig.byte_size || '',
          title: this.$t('FILE_STORAGE.LIST.TABLE_HEADER.SIZE'),
          align: this.isRTLView ? 'right' : 'left',
        },
        {
          field: 'location',
          key: 'location',
          title: this.$t('FILE_STORAGE.LIST.TABLE_HEADER.LOCATION'),
          align: this.isRTLView ? 'right' : 'left',
          renderBodyCell: ({ row }) => {
            return row.conversationUrl ? (
              <a href={row.conversationUrl} target="_blank">
                {row.location}
              </a>
            ) : (
              row.location
            );
          },
        },
        {
          field: 'created_at',
          key: 'created_at',
          sortBy: this.sortConfig.created_at || '',
          title: this.$t('FILE_STORAGE.LIST.TABLE_HEADER.ADDED_DATE'),
          align: this.isRTLView ? 'right' : 'left',
        },
      ];
    },
    updateColumnTitles() {
      const titles = {
        filename: this.$t('FILE_STORAGE.LIST.TABLE_HEADER.FILENAME'),
        byte_size: this.$t('FILE_STORAGE.LIST.TABLE_HEADER.SIZE'),
        location: this.$t('FILE_STORAGE.LIST.TABLE_HEADER.LOCATION'),
        created_at: this.$t('FILE_STORAGE.LIST.TABLE_HEADER.ADDED_DATE'),
      };
      this.columns = this.columns.map(col => {
        if (titles[col.field] !== undefined) {
          col.title = titles[col.field];
          col.align = this.isRTLView ? 'right' : 'left';
        }
        return col;
      });
    },
    onPageChange(page) {
      this.$store.dispatch('fileStorage/fetchRecords', { page });
    },
    makeConversationUrl({ id, type }) {
      switch (type) {
        case 'Conversation':
          return `/app/accounts/${this.currentAccountId}/conversations/${id}`;
        case 'Contact':
          return `/app/accounts/${this.currentAccountId}/contacts/${id}`;
        default:
          return null;
      }
    },
    onSelectAllStorage() {
      this.$store.commit('fileStorage/SET_SELECT_ALL_STORAGE', true);
    },
    onClearSelection() {
      this.$store.commit('fileStorage/RESET_SELECTED_ROWS');
      this.checkboxOption.selectedRowKeys = [];
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep {
  td {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ve-table-sort {
    vertical-align: super;
  }
}
.ve-table {
  @apply bg-white dark:bg-slate-900;

  &::v-deep {
    .ve-table-container {
      overflow-y: hidden;
    }
  }
}
</style>
<style lang="scss">
[dir='rtl'] {
  .ve-table {
    .ve-table-container {
      .ve-table-content {
        thead.ve-table-header {
          .ve-table-header-tr {
            .ve-table-header-th {
              .ve-table-sort {
                margin-left: 0;
                margin-right: 5px;
              }
            }
          }
        }
      }
    }
  }
}
</style>
